import { auth } from "../../firebaseConfig";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { useUserStore } from "../store/user";
import { clearTokens } from "./api";

// 자동 로그아웃 처리 헬퍼 함수
const handleAutoLogout = async (userStore) => {
  userStore.setFirebaseAuthEnabled(false);
  userStore.setUid(null);

  // 로그인 상태였다면 로그아웃 처리
  if (userStore.isLogin) {
    console.warn("세션 만료로 인한 자동 로그아웃");
    
    // 토큰 삭제
    clearTokens();
    
    userStore.logOut();
  }
};

// Firebase Auth 상태 변경 감지
export const initAuthStateListener = () => {
  return onAuthStateChanged(auth, async (user) => {
    const userStore = useUserStore();

    if (user) {
      // console.log("Firebase Auth 사용자 로그인됨:", user.email);
      userStore.setFirebaseAuthEnabled(true);
      userStore.setUid(user.uid);

      // 이미 로그인된 상태가 아니라면 UID 정보 업데이트
      if (!userStore.uid) {
        userStore.setUid(user.uid);
      }

      // 토큰 유효성 검증
      try {
        await user.getIdToken();
      } catch (error) {
        console.error("토큰 검증 실패, 로그아웃 처리:", error);
        // 토큰이 유효하지 않으면 로그아웃 처리
        await handleAutoLogout(userStore);
      }
    } else {
      // console.log("Firebase Auth 사용자 로그아웃됨");
      // 세션 만료 또는 로그아웃 시 완전한 로그아웃 처리
      await handleAutoLogout(userStore);
    }
  });
};

// 현재 Firebase Auth 사용자 가져오기
export const getCurrentFirebaseUser = () => {
  return auth.currentUser;
};

// Firebase Auth 로그아웃
export const logoutFromFirebase = async () => {
  try {
    await signOut(auth);
    // console.log("Firebase Auth 로그아웃 성공");
    return true;
  } catch (error) {
    console.error("Firebase Auth 로그아웃 실패:", error);
    return false;
  }
};

// Firebase Auth 토큰 가져오기 (강제 갱신 옵션 추가)
export const getFirebaseAuthToken = async (forceRefresh = false) => {
  const user = auth.currentUser;
  if (user) {
    try {
      // forceRefresh=true이면 강제로 새 토큰 발급
      return await user.getIdToken(forceRefresh);
    } catch (error) {
      console.error("Firebase Auth 토큰 가져오기 실패:", error);

      // 토큰 만료 관련 에러인 경우 강제 갱신 시도
      if (error.code === "auth/id-token-expired" && !forceRefresh) {
        // console.log("토큰 만료됨, 강제 갱신 시도");
        try {
          return await getFirebaseAuthToken(true);
        } catch (retryError) {
          // 강제 갱신도 실패하면 로그아웃 처리
          console.error("토큰 갱신 실패, 자동 로그아웃 처리:", retryError);
          const userStore = useUserStore();
          await handleAutoLogout(userStore);
          return null;
        }
      }

      // 다른 인증 에러의 경우에도 로그아웃 처리
      if (error.code?.startsWith("auth/")) {
        console.error("인증 오류 발생, 자동 로그아웃 처리:", error.code);
        const userStore = useUserStore();
        await handleAutoLogout(userStore);
      }

      return null;
    }
  }
  return null;
};

// Firebase Auth 연결 상태 확인
export const checkAuthConnection = async () => {
  try {
    const user = auth.currentUser;
    if (!user) {
      // console.log("사용자가 로그인되지 않음");
      const userStore = useUserStore();
      if (userStore.isLogin) {
        // 로그인 상태인데 Firebase user가 없으면 로그아웃 처리
        await handleAutoLogout(userStore);
      }
      return false;
    }

    // 토큰 가져오기 시도로 연결 상태 확인
    const token = await getFirebaseAuthToken();
    if (!token) {
      // console.log("토큰 가져오기 실패 - 연결 문제 가능성");
      const userStore = useUserStore();
      await handleAutoLogout(userStore);
      return false;
    }

    // console.log("Firebase Auth 연결 정상");
    return true;
  } catch (error) {
    console.error("Firebase Auth 연결 확인 실패:", error);
    const userStore = useUserStore();
    await handleAutoLogout(userStore);
    return false;
  }
};
